import html from "nanohtml";
import {
  generateResponsiveImageData,
  ASPECT_RATIOS,
  type Ratio,
  type Cropping,
} from "../../utils/imageUtils.ts";

export interface ResponsiveImageProps {
  path?: string;
  fallbackUrl?: string;
  aspectRatio?: Ratio;
  cropping?: Cropping | null;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}

export function ResponsiveImage(props: ResponsiveImageProps): HTMLElement {
  const {
    path,
    fallbackUrl,
    aspectRatio = ASPECT_RATIOS.square,
    cropping,
    alt,
    className = "",
    loading = "lazy",
  } = props;

  // Generate responsive image data if path is provided
  const responsiveImageData = path
    ? generateResponsiveImageData({
        path,
        aspectRatio,
        cropping,
      })
    : null;

  // If we have responsive image data, render picture element
  if (responsiveImageData) {
    return html`
      <picture class="rw-flex">
        ${responsiveImageData.sources.map(
          (source) => html`
            <source srcset="${source.srcset}" media="${source.media}" />
          `
        )}
        <img
          src="${responsiveImageData.fallbackSrc}"
          alt="${alt}"
          class="${className}"
          loading="${loading}"
        />
      </picture>
    `;
  }

  // Fallback to regular img if fallbackUrl is provided
  if (fallbackUrl) {
    return html`
      <img
        src="${fallbackUrl}"
        alt="${alt}"
        class="${className}"
        loading="${loading}"
      />
    `;
  }

  // No image available, return empty div with proper accessibility
  return html`
    <div 
      class="${className}" 
      role="img" 
      aria-label="No image available"
      aria-hidden="true"
    ></div>
  `;
} 